import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Button,
} from "@mui/material";
import { reportCategoryMap } from "./reportConfig";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

const categoryDisplayOrder = ["Devices", "Vehicles", "Routes", "Stops", "Drivers", "Users"];

const sortCategories = (categories) =>
  [...categories].sort((a, b) => {
    const aIndex = categoryDisplayOrder.indexOf(a.key);
    const bIndex = categoryDisplayOrder.indexOf(b.key);
    return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
  });

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

const CategoryDropdown = React.memo(
  ({
    categoryKey,
    data,
    selectedData,
    search,
    onSearchChange,
    onDropdownClose,
    onAllToggle,
    onSelectAll,
    onClearAll,
    onItemSelection,
    compact = false,
    t = (value) => value,
  }) => {
    const filteredData = useMemo(
      () =>
        data.filter((item) => {
          const searchStr = search.toLowerCase();
          const nameStr = String(item.name ?? "").toLowerCase();
          const displayNameStr = String(item.displayName ?? "").toLowerCase();
          const idStr = String(item.id ?? "").toLowerCase();
          return (
            nameStr.includes(searchStr) ||
            displayNameStr.includes(searchStr) ||
            idStr.includes(searchStr)
          );
        }),
      [data, search]
    );

    return (
      <Box
        sx={{
          border: "1px solid #183059",
          borderRadius: compact ? "6px" : "12px",
          maxHeight: 240,
          position: "absolute",
          minWidth: compact ? 0 : 300,
          width: compact ? "100%" : undefined,
          maxWidth: compact ? "100%" : undefined,
          top: "100%",
          left: 0,
          zIndex: 10,
          backgroundColor: "white",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          mt: 1, // Margin to align with search bar
          p: compact ? 0.75 : 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <IconButton
            onClick={onDropdownClose}
            size="small"
            sx={{ color: "#183059" }}
          >
            <ClearIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={onSelectAll}
            disabled={
              data.length === 0 || selectedData.selectedItems.length === data.length
            }
            sx={{
              flex: 1,
              minHeight: 28,
              borderColor: "#183059",
              color: "#183059",
              fontSize: compact ? "10px" : "12px",
              textTransform: "none",
              "&.Mui-disabled": {
                borderColor: "#CBD5E1",
                color: "#94A3B8",
              },
            }}
          >
            {t("Select All")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={onClearAll}
            disabled={selectedData.selectedItems.length === 0}
            sx={{
              flex: 1,
              minHeight: 28,
              borderColor: "#64748B",
              color: "#64748B",
              fontSize: compact ? "10px" : "12px",
              textTransform: "none",
              "&.Mui-disabled": {
                borderColor: "#CBD5E1",
                color: "#94A3B8",
              },
            }}
          >
            {t("Clear All")}
          </Button>
        </Box>
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "calc(240px - 40px)", // Adjust for header and padding
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb:hover": { background: "#666" },
            scrollbarWidth: "thin",
            scrollbarColor: "#888 transparent",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1, minWidth: 0 }}>
            <Checkbox
              checked={
                selectedData.selectedItems.length === data.length &&
                data.length > 0
              }
              onChange={onAllToggle}
              size="small"
              sx={{
                color: "#20A8E0",
                "&.Mui-checked": {
                  color: "#20A8E0",
                  backgroundColor: "#fff",
                },
              }}
            />
            <ListItemText
              primary={`${t("All")} ${t(categoryKey)}`}
              primaryTypographyProps={{
                sx: {
                  fontSize: compact ? "12px" : undefined,
                  overflowWrap: "anywhere",
                },
              }}
            />
          </Box>
          {filteredData.map((item) => (
            <Box
              key={item.id}
              sx={{ display: "flex", alignItems: "center", mb: 1, minWidth: 0 }}
            >
              <Checkbox
                checked={selectedData.selectedItems.includes(item.name)}
                onChange={() => onItemSelection(item.name)}
                size="small"
                sx={{
                  color: "#20A8E0",
                  "&.Mui-checked": {
                    color: "#20A8E0",
                    backgroundColor: "#fff",
                  },
                }}
              />
              <ListItemText
                primary={item.displayName}
                primaryTypographyProps={{
                  sx: {
                    fontSize: compact ? "12px" : undefined,
                    overflowWrap: "anywhere",
                  },
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }
);

const CategorySelector = ({
  reportType,
  onCategoryChange,
  vehicles = [],
  routes = [],
  drivers = [],
  stops = [],
  users = [],
  devices = [],
  initialCategories = {},
  showFieldLabels = false,
  t = (value) => value,
}) => {
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("");
  const [selectedCategories, setSelectedCategories] = useState({});
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const availableCategories = useMemo(
    () => sortCategories(reportCategoryMap[reportType] || []),
    [reportType]
  );

  const categoryDataMap = useMemo(
    () => ({
      Routes: routes.map((r) => ({
        id: r.id,
        name: r.name || r.route_name || "",
        displayName: r.name || r.route_name || "",
      })),
      Vehicles: vehicles.map((v) => ({
        id: v.device_id,
        name: v.VehicleNumber || v.device_id || "",
        displayName: v.name || v.VehicleNumber || "",
      })),
      Devices: devices.map((device) => ({
        id: device.id || device.deviceId,
        name: device.deviceId || String(device.id || ""),
        displayName: device.deviceId || device.name || String(device.id || ""),
      })),
      Drivers: drivers.map((d) => ({
        id: d.id,
        name: `${d.first_name || ""} ${d.last_name || ""}`.trim(),
        displayName: `${d.first_name || ""} ${d.last_name || ""}`.trim(),
      })),
      Stops: stops.map((s) => ({
        id: s.id,
        name: s.name || "",
        displayName: s.name || "",
      })),
      Users: Array.isArray(users)
        ? users.map((u) => ({
            id: u.id,
            name: u.name || u.userName || u.email || "",
            displayName: u.name || u.userName || u.email || "",
          }))
        : [],
    }),
    [routes, vehicles, drivers, stops, users, devices]
  );

  // useEffect(() => {
  //   setSelectedCategoryKey("");
  //   setSelectedCategories({});
  //   setSearch("");
  //   setShowDropdown(false);
  //   onCategoryChange({});
  // }, [reportType, onCategoryChange]);

  const prevReportTypeRef = useRef();

  useEffect(() => {
    if (
      prevReportTypeRef.current !== undefined &&
      prevReportTypeRef.current !== reportType
    ) {
      // Only clear when reportType changes, not on initial mount or re-renders
      setSelectedCategoryKey("");
      setSelectedCategories({});
      setSearch("");
      setShowDropdown(false);
      onCategoryChange({});
    }
    prevReportTypeRef.current = reportType;
  }, [reportType]);

  useEffect(() => {
    if (Object.keys(initialCategories).length > 0) {
      setSelectedCategories(initialCategories);
      const categoryKey = Object.keys(initialCategories)[0];
      if (categoryKey) {
        setSelectedCategoryKey(categoryKey);
      }
    }
  }, [initialCategories]);

  const handleCategoryKeyChange = useCallback(
    (newCategoryKeys) => {
      const keys = Array.isArray(newCategoryKeys)
        ? newCategoryKeys
        : newCategoryKeys
        ? [newCategoryKeys]
        : [];
      const validKeys = keys.filter(
        (key) =>
          typeof key === "string" &&
          availableCategories.some((category) => category.key === key)
      );

      const nextCategories = validKeys.reduce((acc, key) => {
        const data = categoryDataMap[key] || [];
        acc[key] = selectedCategories[key] || {
          isAllSelected: true,
          selectedItems: data.map((item) => item.name),
          required:
            availableCategories.find((category) => category.key === key)
              ?.required || false,
        };
        return acc;
      }, {});

      setSelectedCategoryKey((currentKey) =>
        validKeys.includes(currentKey) ? currentKey : validKeys[0] || ""
      );
      setSelectedCategories(nextCategories);
      setSearch("");
      setShowDropdown(false);
      onCategoryChange(nextCategories);
    },
    [
      availableCategories,
      categoryDataMap,
      onCategoryChange,
      selectedCategories,
    ]
  );

  const handleSelectAllCategories = useCallback(
    (event) => {
      event?.preventDefault();
      event?.stopPropagation();
      handleCategoryKeyChange(availableCategories.map((category) => category.key));
    },
    [availableCategories, handleCategoryKeyChange]
  );

  const handleClearAllCategories = useCallback(
    (event) => {
      event?.preventDefault();
      event?.stopPropagation();
      handleCategoryKeyChange([]);
    },
    [handleCategoryKeyChange]
  );

  const debouncedSearchChange = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
      }, 300),
    []
  );

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      debouncedSearchChange(value);
      setShowDropdown(true);
    },
    [debouncedSearchChange]
  );

  const handleDropdownClose = useCallback(() => {
    setSearch("");
    setShowDropdown(false);
  }, []);

  const handleSearchIconClick = useCallback(() => {
    setShowDropdown(true);
  }, []);

  const handleAllToggle = useCallback(() => {
    const data = categoryDataMap[selectedCategoryKey] || [];
    const selectedData = selectedCategories[selectedCategoryKey] || {
      isAllSelected: false,
      selectedItems: [],
      required:
        availableCategories.find((c) => c.key === selectedCategoryKey)
          ?.required || false,
    };

    const allSelected =
      selectedData.selectedItems.length === data.length && data.length > 0;
    const newSelectedItems = allSelected ? [] : data.map((item) => item.name);

    const newCategoryData = {
      ...selectedCategories,
      [selectedCategoryKey]: {
        ...selectedData,
        isAllSelected: !allSelected,
        selectedItems: newSelectedItems,
      },
    };

    setSelectedCategories(newCategoryData);
    onCategoryChange(newCategoryData);
  }, [
    selectedCategoryKey,
    categoryDataMap,
    selectedCategories,
    availableCategories,
    onCategoryChange,
  ]);

  const setAllItemsForSelectedCategory = useCallback(
    (shouldSelectAll) => {
      const data = categoryDataMap[selectedCategoryKey] || [];
      const selectedData = selectedCategories[selectedCategoryKey] || {
        isAllSelected: false,
        selectedItems: [],
        required:
          availableCategories.find((c) => c.key === selectedCategoryKey)
            ?.required || false,
      };

      const newCategoryData = {
        ...selectedCategories,
        [selectedCategoryKey]: {
          ...selectedData,
          isAllSelected: shouldSelectAll,
          selectedItems: shouldSelectAll ? data.map((item) => item.name) : [],
        },
      };

      setSelectedCategories(newCategoryData);
      onCategoryChange(newCategoryData);
    },
    [
      selectedCategoryKey,
      categoryDataMap,
      selectedCategories,
      availableCategories,
      onCategoryChange,
    ]
  );

  const handleItemSelection = useCallback(
    (itemName) => {
      const selectedData = selectedCategories[selectedCategoryKey] || {
        isAllSelected: false,
        selectedItems: [],
        required:
          availableCategories.find((c) => c.key === selectedCategoryKey)
            ?.required || false,
      };

      const data = categoryDataMap[selectedCategoryKey] || [];
      let newSelectedItems;

      if (selectedData.selectedItems.includes(itemName)) {
        newSelectedItems = selectedData.selectedItems.filter(
          (i) => i !== itemName
        );
      } else {
        newSelectedItems = [...selectedData.selectedItems, itemName];
      }

      const newCategoryData = {
        ...selectedCategories,
        [selectedCategoryKey]: {
          ...selectedData,
          isAllSelected: newSelectedItems.length === data.length,
          selectedItems: newSelectedItems,
        },
      };

      setSelectedCategories(newCategoryData);
      onCategoryChange(newCategoryData);
    },
    [
      selectedCategoryKey,
      selectedCategories,
      categoryDataMap,
      availableCategories,
      onCategoryChange,
    ]
  );

  const selectedData = selectedCategories[selectedCategoryKey] || {
    isAllSelected: false,
    selectedItems: [],
    required:
      availableCategories.find((c) => c.key === selectedCategoryKey)
        ?.required || false,
  };

  const selectedDisplayItems = useMemo(() => {
    const data = categoryDataMap[selectedCategoryKey] || [];
    return selectedData.selectedItems.map((selectedItem) => {
      const item = data.find((option) => option.name === selectedItem);
      return item?.displayName || selectedItem;
    });
  }, [categoryDataMap, selectedCategoryKey, selectedData.selectedItems]);

  useEffect(() => {
    console.log("Stops prop:", stops);
  }, [stops]);

  const selectedCategoryKeys = Object.keys(selectedCategories).filter((key) =>
    availableCategories.some((category) => category.key === key)
  );
  const allSelectedCategoriesLabel = `${t("All")} ${t("selected")} ${t("Categories")}`;
  const allItemsLabel = selectedCategoryKey
    ? `${t("All")} ${t(selectedCategoryKey)}`
    : "";
  const selectedItemsLabel = (count) =>
    `${count} ${t(selectedCategoryKey)} ${t("selected")}`;

  return (
    <Box
      className="category-selector"
      sx={{
        display: "flex",
        gap: 2,
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        minWidth: 0,
        width: { xs: "100%", sm: "auto" },
        flex: { xs: "1 1 100%", sm: "0 1 auto" },
      }}
    >
      <Box className="report-filter-control" sx={{ width: "100%" }}>
        {showFieldLabels && (
          <Typography className="report-filter-label">{t("Category")}</Typography>
        )}
        <FormControl
          sx={{
            minWidth: { xs: 0, sm: showFieldLabels ? 0 : 160 },
            width: "100%",
          }}
        >
          {/* <InputLabel sx={{ backgroundColor: "#fff", mt: "-4px" }}>
            Select an Category
          </InputLabel> */}
          <Select
            multiple
            value={selectedCategoryKeys}
            placeholder={t("Select an Category")}
            displayEmpty
            label={showFieldLabels ? undefined : t("Select an Category")}
            onChange={(e) => handleCategoryKeyChange(e.target.value)}
            renderValue={(selected) => {
              if (!selected || selected.length === 0) return t("Select an Category");
              if (selected.length === 1) return t(selected[0]);
              return `${selected.length} ${t("Categories")}`;
            }}
            sx={{
              "& fieldset": {
                border: showFieldLabels ? undefined : "none",
              },
              backgroundColor: "#FFFFFF",
              height: showFieldLabels ? "30px" : "46px",
              border: showFieldLabels ? undefined : "1px solid #183059",
              borderRadius: showFieldLabels ? "4px" : "12px",
              overflow: "hidden",
              color: showFieldLabels ? "#000000" : undefined,
              fontFamily: showFieldLabels ? "Inter, sans-serif" : undefined,
              fontSize: showFieldLabels ? "12px" : undefined,
              fontWeight: showFieldLabels ? 400 : undefined,
              lineHeight: showFieldLabels ? "16px" : undefined,
              "& .MuiSelect-select": {
                py: showFieldLabels ? "6px" : 1,
                px: showFieldLabels ? "6px" : undefined,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                px: 1,
                py: 1,
                borderBottom: "1px solid #E5E7EB",
              }}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAllCategories}
                disabled={
                  availableCategories.length === 0 ||
                  selectedCategoryKeys.length === availableCategories.length
                }
                sx={{
                  flex: 1,
                  minHeight: 28,
                  borderColor: "#183059",
                  color: "#183059",
                  fontSize: "12px",
                  textTransform: "none",
                  "&.Mui-disabled": {
                    borderColor: "#CBD5E1",
                    color: "#94A3B8",
                  },
                }}
              >
                {t("Select All")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleClearAllCategories}
                disabled={selectedCategoryKeys.length === 0}
                sx={{
                  flex: 1,
                  minHeight: 28,
                  borderColor: "#64748B",
                  color: "#64748B",
                  fontSize: "12px",
                  textTransform: "none",
                  "&.Mui-disabled": {
                    borderColor: "#CBD5E1",
                    color: "#94A3B8",
                  },
                }}
              >
                {t("Clear All")}
              </Button>
            </Box>
            {availableCategories.map((category) => (
              <MenuItem key={category.key} value={category.key}>
                <Checkbox
                  size="small"
                  checked={selectedCategoryKeys.includes(category.key)}
                  sx={{
                    p: 0,
                    mr: 1,
                    color: "#20A8E0",
                    "&.Mui-checked": { color: "#20A8E0" },
                  }}
                />
                {t(category.key)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {selectedCategoryKeys.length > 1 && (
        <Box className="report-filter-control" sx={{ width: "100%" }}>
          {showFieldLabels && (
            <Typography className="report-filter-label">
              {t("Filter Items")}
            </Typography>
          )}
          <TextField
            value={allSelectedCategoriesLabel}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            sx={{
              mb: 0,
              backgroundColor: "#FFFFFF",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: showFieldLabels ? "4px" : "12px",
                height: showFieldLabels ? "30px" : "46px",
                color: showFieldLabels ? "#000000" : undefined,
                fontFamily: showFieldLabels ? "Inter, sans-serif" : undefined,
                fontSize: showFieldLabels ? "12px" : undefined,
                fontWeight: showFieldLabels ? 400 : undefined,
                lineHeight: showFieldLabels ? "16px" : undefined,
                "& input": {
                  padding: showFieldLabels ? "6px" : undefined,
                },
              },
            }}
          />
        </Box>
      )}
      {selectedCategoryKeys.length === 1 && selectedCategoryKey && (
        <Box
          className="report-filter-control"
          sx={{
            minWidth: { xs: 0, sm: 160 },
            position: "relative",
            width: "100%",
          }}
        >
          {showFieldLabels && (
            <Typography className="report-filter-label">
              {t("Filter Items")}
            </Typography>
          )}
          <Box
            sx={{
              minWidth: 0,
              width: "100%",
              position: "relative",
              borderRadius: "12px",
              backgroundColor: "#FFFFFF",
            }}
          >
            <TextField
              placeholder={allItemsLabel}
              variant="outlined"
              size="small"
              fullWidth
              value={
                selectedData.isAllSelected
                  ? allItemsLabel
                  : selectedDisplayItems.length > 0
                  ? selectedDisplayItems.length <= 3
                    ? selectedDisplayItems.join(", ")
                    : selectedItemsLabel(selectedDisplayItems.length)
                  : search || ""
              }
              onChange={handleSearchChange}
              onClick={handleSearchIconClick}
              sx={{
                mb: 0,
                borderRadius: showFieldLabels ? "4px" : "12px",
                backgroundColor: "#FFFFFF",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: showFieldLabels ? "4px" : "12px",
                  border: showFieldLabels ? undefined : "1px solid #183059",
                  height: showFieldLabels ? "30px" : "46px",
                  overflow: "hidden",
                  color: showFieldLabels ? "#000000" : undefined,
                  fontFamily: showFieldLabels ? "Inter, sans-serif" : undefined,
                  fontSize: showFieldLabels ? "12px" : undefined,
                  fontWeight: showFieldLabels ? 400 : undefined,
                  lineHeight: showFieldLabels ? "16px" : undefined,
                  "& fieldset": {
                    border: showFieldLabels ? undefined : "none",
                  },
                  "& input": {
                    padding: showFieldLabels ? "6px" : undefined,
                  },
                },
              }}
              autoComplete="off"
              InputProps={{
                autoComplete: "off",
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSearchIconClick}
                      size="small"
                      sx={{ visibility: !showDropdown ? "visible" : "hidden" }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {showDropdown && (
            <CategoryDropdown
              categoryKey={selectedCategoryKey}
              data={categoryDataMap[selectedCategoryKey] || []}
              selectedData={selectedData}
              search={search}
              onSearchChange={handleSearchChange}
              onDropdownClose={handleDropdownClose}
              onAllToggle={handleAllToggle}
              onSelectAll={() => setAllItemsForSelectedCategory(true)}
              onClearAll={() => setAllItemsForSelectedCategory(false)}
              onItemSelection={handleItemSelection}
              compact={showFieldLabels}
              t={t}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CategorySelector;
